type SettledListener = (issueId: string) => void;
type WriteScope = { pending: Map<string, number>; listeners: Set<SettledListener> };

const scopes = new Map<string, WriteScope>();
const scopeKey = (workspaceId: string, userId: string) => JSON.stringify([workspaceId, userId]);

function getScope(key: string): WriteScope {
  let scope = scopes.get(key);
  if (!scope) {
    scope = { pending: new Map(), listeners: new Set() };
    scopes.set(key, scope);
  }
  return scope;
}

function prune(key: string, scope: WriteScope) {
  if (!scope.pending.size && !scope.listeners.size && scopes.get(key) === scope) scopes.delete(key);
}

/** Begin synchronously, before publishing an optimistic task change. The
 * captured viewer/workspace never changes when the browser switches scope.
 */
export function beginTaskWrite(workspaceId: string, userId: string, issueId: string): () => void {
  const key = scopeKey(workspaceId, userId);
  const scope = getScope(key);
  scope.pending.set(issueId, (scope.pending.get(issueId) ?? 0) + 1);
  let finished = false;
  return () => {
    if (finished) return;
    finished = true;
    const remaining = (scope.pending.get(issueId) ?? 1) - 1;
    if (remaining) {
      scope.pending.set(issueId, remaining);
      return;
    }
    scope.pending.delete(issueId);
    for (const listener of [...scope.listeners]) {
      // A view callback must not prevent another waiter or the write cleanup.
      try { listener(issueId); } catch { /* No provider data is involved. */ }
    }
    prune(key, scope);
  };
}

/** Fires once the last overlapping write to an issue has settled, including
 * failed writes. Settlement is an instruction to re-read, not proof of success.
 */
export function subscribeTaskWrites(workspaceId: string, userId: string, onSettled: SettledListener): () => void {
  const key = scopeKey(workspaceId, userId);
  const scope = getScope(key);
  scope.listeners.add(onSettled);
  return () => {
    scope.listeners.delete(onSettled);
    prune(key, scope);
  };
}

function aborted(): Error {
  const error = new Error("Task write wait aborted.");
  error.name = "AbortError";
  return error;
}

/** Wait for all outstanding writes in this exact scope. Re-check after every
 * settlement so writes added during the wait also finish before the read.
 */
export async function waitForTaskWrites(workspaceId: string, userId: string, signal?: AbortSignal): Promise<void> {
  const key = scopeKey(workspaceId, userId);
  for (;;) {
    if (signal?.aborted) throw aborted();
    if (!scopes.get(key)?.pending.size) return;
    await new Promise<void>((resolve, reject) => {
      const cleanup = () => { unsubscribe(); signal?.removeEventListener("abort", onAbort); };
      const onAbort = () => { cleanup(); reject(aborted()); };
      const unsubscribe = subscribeTaskWrites(workspaceId, userId, () => { cleanup(); resolve(); });
      signal?.addEventListener("abort", onAbort, { once: true });
    });
  }
}
